/**
 * The slice of the origin-private filesystem cuekards uses, as ports.
 *
 * Narrow and structural, for the same reason `RecorderLike` is: everything that
 * touches storage then has a shape a fake can take, and the capture path becomes
 * testable in Node rather than only in a browser. The real handles satisfy these
 * shapes; the cast that says so lives at the one place the platform is reached
 * for — see {@link opfsRoot}.
 *
 * They are declared here rather than taken from a library because they are not
 * in one. `FileSystemSyncAccessHandle` is a *worker* interface, and this package
 * compiles against the DOM lib, so the type is absent at exactly the point the
 * design depends on it existing.
 *
 * ## Why the origin-private filesystem at all
 *
 * The obvious design — write straight into the file the user picked — is wrong,
 * and it is worth stating why once. That path is buffered and asynchronous and
 * commits when the file is closed, so a crash mid-take loses everything, which
 * defeats the entire reason the recording engine writes continuously (FR-45).
 * Origin-private storage is the only place in a browser that offers *synchronous*
 * writes, from a worker, which is a direct analogue of the descriptor the desktop
 * opens — and it survives a reload, which is what makes crash recovery possible
 * at all (web ticket 13). The save dialog is used once, at delivery.
 */

/** A file open for synchronous writing — the whole reason this storage was
 * chosen. Only obtainable in a worker. */
export interface SyncFileHandle {
  write(data: ArrayBufferView | ArrayBuffer, options?: { at?: number }): number;
  /** Push the writes at the browser's own durability boundary. The analogue of
   * `fsync`, and the expensive call, so it runs on a cadence. */
  flush(): void;
  truncate(size: number): void;
  close(): void;
}

/**
 * A file open for reading — the `File`-shaped half of a handle.
 *
 * Available on the window as well as in a worker, unlike the synchronous write
 * handle above, which is why the two halves of this package live where they do:
 * the worker writes a take and the page reads it back out to deliver it. It is
 * also how a take's size is found without opening it for writing, which matters
 * for the same reason — a sync access handle is an exclusive lock, and taking one
 * on the capture of a take in progress would fail.
 */
export interface OpfsFile {
  readonly size: number;
  text(): Promise<string>;
  arrayBuffer(): Promise<ArrayBuffer>;
}

export interface OpfsFileHandle {
  createSyncAccessHandle(): Promise<SyncFileHandle>;
  getFile(): Promise<OpfsFile>;
}

/** One entry in a directory, as its own listing gives it. */
export interface OpfsEntryHandle {
  readonly kind: "file" | "directory";
}

export interface OpfsDirectoryHandle {
  getDirectoryHandle(
    name: string,
    options?: { create?: boolean },
  ): Promise<OpfsDirectoryHandle>;
  getFileHandle(
    name: string,
    options?: { create?: boolean },
  ): Promise<OpfsFileHandle>;
  removeEntry(name: string, options?: { recursive?: boolean }): Promise<void>;
  /**
   * What is in here, by name and kind.
   *
   * Asynchronously iterable rather than an array, which is the shape the platform
   * offers and not a stylistic choice: a directory listing in this API is
   * produced lazily. What reads it — the recovery scan — is the one thing in
   * cuekards that has to find sessions it did not itself create, because the run
   * that created them is the run that was interrupted.
   */
  entries(): AsyncIterable<[string, OpfsEntryHandle]>;
}

/** Where a directory is asked for, and what to do when it is not there. */
export type Create = { create?: boolean };

/**
 * The origin's private root.
 *
 * The one cast in the arrangement, and it is here rather than spread through the
 * code that uses it: `navigator.storage.getDirectory()` returns the real handles,
 * which have every member above and a good deal more, and the type for the
 * synchronous one is only declared in the worker lib this package does not
 * compile against.
 */
export function opfsRoot(): Promise<OpfsDirectoryHandle> {
  return navigator.storage.getDirectory() as unknown as Promise<OpfsDirectoryHandle>;
}

/**
 * Walk to a directory, creating it or not.
 *
 * A path is a list of names rather than a slash-separated string because that is
 * what the API takes, and joining and re-splitting one would be inventing a
 * separator for a filesystem that has no notion of one.
 */
export async function directoryAt(
  root: OpfsDirectoryHandle,
  path: readonly string[],
  options?: Create,
): Promise<OpfsDirectoryHandle> {
  let at = root;
  for (const name of path) at = await at.getDirectoryHandle(name, options);
  return at;
}
